import R from 'ramda';
// Removed wQuery import - using manual query string builder instead
import {
    FETCH_DELETE_PARAMS,
    FETCH_PATCH_PARAMS,
    FETCH_POST_PARAMS,
    FETCH_PUT_PARAMS,
    FILE_UPLOAD_PARAMS, getFetchParams, FORM_METHOD,
} from '../utils';
import {getStore} from '../utils/store';
import {showToast} from '../incab/common';

export class ApiError {
    constructor(status, message) {
        this.status = status;
        this.message = message;
        this.data = {}; // Always initialize data as an object
        this.stack = (new Error()).stack;
    }

    getMessage() {
        return this.message || `Code ${this.status}`;
    }
}

// Field validation rules mapping
const FIELD_RULES = {
    email: 'Обязательное поле. Должен быть валидным email адресом (например: user@example.com)',
    name: 'Обязательное поле. Минимум 2 символа, максимум 30 символов. Только буквы, пробелы, дефисы, апострофы и точки',
    phone: 'Обязательное поле. Должен быть валидным номером телефона с кодом страны в международном формате (например: +420 774 287 541)',
    password: 'Требуется, если вы уже регистрировались с этим email',
    startComment: 'Обязательное поле. Укажите желаемое время проведения занятия',
    freeTime: 'Обязательное поле. Выберите дату и время урока из календаря',
    comment: 'Обязательное поле. Укажите желаемое время проведения занятия',
};

// Field labels mapping
const FIELD_LABELS = {
    email: 'Электронная почта',
    name: 'Имя',
    phone: 'Телефон',
    password: 'Пароль',
    startComment: 'Желаемое время проведения занятия',
    freeTime: 'Дата и время урока',
    comment: 'Желаемое время проведения занятия',
};

const formatValidationErrors = (data) => {
    if (!data || typeof data !== 'object') {
        return 'Ошибка валидации данных';
    }

    // Handle non-field errors
    if (data.non_field_errors) {
        const nonFieldError = Array.isArray(data.non_field_errors) 
            ? data.non_field_errors[0] 
            : data.non_field_errors;
        return nonFieldError;
    }

    // Get all field errors
    const fieldErrors = [];
    Object.keys(data).forEach(fieldName => {
        if (fieldName === '__onClick') return;
        
        const error = data[fieldName];
        const errorMessage = Array.isArray(error) ? error[0] : error;
        const fieldLabel = FIELD_LABELS[fieldName] || fieldName;
        const fieldRule = FIELD_RULES[fieldName] || 'Проверьте правильность заполнения';
        
        fieldErrors.push({
            field: fieldLabel,
            error: errorMessage,
            rule: fieldRule,
        });
    });

    if (fieldErrors.length === 0) {
        return 'Исправьте все ошибки, выделенные красным цветом';
    }

    // Format error message with fields and rules
    // Use HTML formatting for better display in toast notifications
    let message = '<strong>Ошибка валидации. Исправьте следующие поля:</strong><br><br>';
    fieldErrors.forEach(({field, error, rule}, index) => {
        message += `<strong>${index + 1}. ${field}</strong><br>`;
        message += `&nbsp;&nbsp;Ошибка: ${error}<br>`;
        message += `&nbsp;&nbsp;Требования: ${rule}<br>`;
        if (index < fieldErrors.length - 1) {
            message += '<br>';
        }
    });

    return message;
};

export class BadRequest extends ApiError {
    constructor(data, msg) {
        const defaultMessage = formatValidationErrors(data);
        super(
            400,
            msg || defaultMessage
        );
        this.data = data;
    }

    getDetailedMessage() {
        return formatValidationErrors(this.data);
    }
}

const processResponse = response => {
    console.log('[API Utils] processResponse called with status:', response.status);
    const processStartTime = Date.now();
    switch (response.status) {
        case 200:
        case 201:
        case 204:
            console.log('[API Utils] Success response:', response.status);
            return response;
        case 404:
            console.log('[API Utils] Not Found (404), parsing JSON...');
            // Clone response to avoid "body already read" error
            const cloned404Response = response.clone();
            return response.json().then(data => {
                console.log('[API Utils] Parsed 404 error data:', data);
                const error = new ApiError(404, data.detail || data.message || 'Ресурс не найден');
                error.data = data;
                throw error;
            }).catch(err => {
                console.error('[API Utils] Error parsing 404 response JSON:', err);
                // If error is already an ApiError, re-throw it
                if (err instanceof ApiError) {
                    throw err;
                }
                // Try to parse from cloned response as text
                return cloned404Response.text().then(text => {
                    console.log('[API Utils] Response text length:', text ? text.length : 0);
                    let data = {};
                    try {
                        if (text && text.trim()) {
                            data = JSON.parse(text);
                        }
                    } catch (parseErr) {
                        console.error('[API Utils] Cannot parse text as JSON:', parseErr);
                    }
                    const error = new ApiError(404, data.detail || data.message || 'Ресурс не найден');
                    error.data = data;
                    throw error;
                }).catch(() => {
                    // Final fallback
                    const error = new ApiError(404, 'Ресурс не найден');
                    error.data = {};
                    throw error;
                });
            });
        case 400:
            console.log('[API Utils] Bad Request (400), parsing JSON...');
            // Clone response to avoid "body already read" error
            const clonedResponse = response.clone();
            return response.json().then(data => {
                console.log('[API Utils] Parsed 400 error data:', data);
                const msg =
                    data.teacher
                        ? data.teacher[0]
                        : data.student
                            ? data.student[0]
                            : data.detail
                                ? data.detail[0]
                                : null
                console.log('[API Utils] Creating BadRequest with data:', data, 'msg:', msg);
                const badRequest = new BadRequest(data, msg);
                console.log('[API Utils] BadRequest object created:', badRequest);
                console.log('[API Utils] BadRequest.data:', badRequest.data);
                console.log('[API Utils] BadRequest.status:', badRequest.status);
                throw badRequest;
            }).catch(err => {
                console.error('[API Utils] Error parsing 400 response JSON:', err);
                console.error('[API Utils] Error type:', typeof err);
                console.error('[API Utils] Error constructor:', err.constructor.name);
                console.error('[API Utils] Error instanceof BadRequest:', err instanceof BadRequest);
                
                // If error is already a BadRequest with data, preserve it
                if (err instanceof BadRequest) {
                    console.log('[API Utils] Error is already BadRequest');
                    console.log('[API Utils] BadRequest.data:', err.data);
                    if (err.data && Object.keys(err.data).length > 0) {
                        console.log('[API Utils] BadRequest has data, re-throwing:', err);
                        throw err;
                    }
                    console.log('[API Utils] BadRequest has no data, will try to get from response');
                }
                
                // Try to parse from cloned response as text
                return clonedResponse.text().then(text => {
                    console.log('[API Utils] Response text length:', text ? text.length : 0);
                    console.log('[API Utils] Response text preview:', text ? text.substring(0, 200) : 'empty');
                    let data = {};
                    try {
                        data = JSON.parse(text);
                        console.log('[API Utils] Parsed data from text:', data);
                    } catch (parseErr) {
                        console.error('[API Utils] Cannot parse text as JSON:', parseErr);
                        console.error('[API Utils] Text that failed to parse:', text);
                    }
                    const badRequest = new BadRequest(data, 'Ошибка валидации данных');
                    console.log('[API Utils] Throwing BadRequest with data:', badRequest.data);
                    throw badRequest;
                }).catch(textErr => {
                    // If text parsing also fails, use empty object
                    console.error('[API Utils] Error getting response text:', textErr);
                    // If we have the original error with data, use it
                    if (err instanceof BadRequest && err.data) {
                        console.log('[API Utils] Using original BadRequest error:', err);
                        throw err;
                    }
                    const badRequest = new BadRequest({}, 'Ошибка валидации данных');
                    console.log('[API Utils] Throwing fallback BadRequest with empty data');
                    throw badRequest;
                });
            });
        case 502:
        case 503:
        case 504:
            // Server errors - try to parse JSON, but handle gracefully if it fails
            return response.text().then(text => {
                let data = {};
                try {
                    data = JSON.parse(text);
                } catch (e) {
                    // Not JSON, use default message
                }
                const error = new ApiError(response.status, data.detail || data.message || 'Сервер временно недоступен. Пожалуйста, попробуйте позже.');
                error.data = data;
                throw error;
            }).catch(() => {
                throw new ApiError(response.status, 'Сервер временно недоступен. Пожалуйста, попробуйте позже.');
            });
        default:
            // For other errors (not 404, 400, 502-504), try to parse JSON response
            return response.text().then(text => {
                let data = {};
                try {
                    if (text && text.trim()) {
                        data = JSON.parse(text);
                    }
                } catch (e) {
                    // Not JSON, use status code
                    console.log('[API Utils] Response is not JSON, using status code', {
                        status: response.status,
                        textPreview: text ? text.substring(0, 200) : 'empty'
                    });
                }
                const errorMessage = (data && (data.detail || data.message)) ? (data.detail || data.message) : `Ошибка ${response.status}`;
                const error = new ApiError(response.status, errorMessage);
                error.data = data || {};
                throw error;
            }).catch((parseError) => {
                console.error('[API Utils] Error parsing error response', {
                    status: response.status,
                    parseError: parseError ? parseError.message : 'unknown error'
                });
                // If it's already an ApiError, re-throw it
                if (parseError instanceof ApiError) {
                    throw parseError;
                }
                const error = new ApiError(response.status, `Ошибка ${response.status}`);
                error.data = {};
                throw error;
            });
    }
};

const processRedirect = json => {
    if (json._redirectUrl) {
        // Redirect to payment URL
        window.location.href = json._redirectUrl;
        return {};
    } else if (json._redirectUrl === '') {
        // Empty redirect URL means success without payment redirect (e.g., demo request)
        // Check if this is a demo booking by checking the current URL
        const isDemoBooking = window.location.href.includes('/demo') || window.location.hash.includes('demo');
        const message = isDemoBooking 
            ? 'Пробный урок успешно забронирован! Мы свяжемся с вами в ближайшее время.'
            : 'Ваша заявка успешно отправлена!';
        const toastAction = showToast('success', message, 'Успешно');
        getStore().dispatch(toastAction);
        return {};
    }
    return json;
};

const commonProcess = response => {
    const startTime = Date.now();
    console.log('[API Utils commonProcess] ===== STARTING JSON PARSING =====', {
        response,
        responseStatus: response.status,
        responseOk: response.ok,
        timestamp: new Date().toISOString()
    });
    return response.json()
        .then(json => {
            const parseDuration = Date.now() - startTime;
            console.log('[API Utils commonProcess] ===== JSON PARSED SUCCESSFULLY =====', {
                json,
                jsonType: typeof json,
                jsonStringified: JSON.stringify(json),
                jsonKeys: json ? Object.keys(json) : null,
                parseDuration: parseDuration + 'ms',
                hasRussian: json && json.russian ? true : false,
                hasNative: json && json.native ? true : false,
                russianData: json && json.russian ? json.russian : null,
                nativeData: json && json.native ? json.native : null,
                timestamp: new Date().toISOString()
            });
            const redirected = processRedirect(json);
            console.log('[API Utils commonProcess] ===== REDIRECT PROCESSED =====', {
                originalJson: json,
                originalJsonStringified: JSON.stringify(json),
                redirected: redirected,
                redirectedStringified: JSON.stringify(redirected),
                timestamp: new Date().toISOString()
            });
            return redirected;
        })
        .catch(error => {
            console.error('[API Utils commonProcess] ===== JSON PARSING FAILED =====', {
                error,
                errorMessage: error.message,
                errorStack: error.stack,
                timestamp: new Date().toISOString()
            });
            throw error;
        });
};

const fetchUrl = (url, params) => {
    console.log('[API Utils fetchUrl] HTTP request initiated', {
        url,
        params,
        method: params ? params.method : 'GET',
        headers: params ? params.headers : null,
        timestamp: new Date().toISOString()
    });
    const startTime = Date.now();
    return fetch(url, params)
        .then(response => {
            const fetchDuration = Date.now() - startTime;
            console.log('[API Utils fetchUrl] HTTP response received', {
                url,
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                ok: response.ok,
                duration: fetchDuration + 'ms',
                timestamp: new Date().toISOString()
            });
            const processStartTime = Date.now();
            const processResult = processResponse(response);
            // processResponse may return a promise or a response object directly
            if (processResult && typeof processResult.then === 'function') {
                // It's a promise
                return processResult.then(result => {
                    const processDuration = Date.now() - processStartTime;
                    const totalDuration = Date.now() - startTime;
                    console.log('[API Utils fetchUrl] Response processed (async)', {
                        url,
                        result,
                        processDuration: processDuration + 'ms',
                        totalDuration: totalDuration + 'ms',
                        timestamp: new Date().toISOString()
                    });
                    return result;
                });
            } else {
                // It's a response object directly (for success cases)
                const processDuration = Date.now() - processStartTime;
                const totalDuration = Date.now() - startTime;
                console.log('[API Utils fetchUrl] Response processed (sync)', {
                    url,
                    result: processResult,
                    processDuration: processDuration + 'ms',
                    totalDuration: totalDuration + 'ms',
                    timestamp: new Date().toISOString()
                });
                return processResult;
            }
        })
        .catch(error => {
            console.error('[API Utils] fetchUrl catch block - error:', error);
            console.error('[API Utils] Error name:', error.name);
            console.error('[API Utils] Error message:', error.message);
            console.error('[API Utils] Error constructor:', error.constructor.name);
            console.error('[API Utils] Error instanceof ApiError:', error instanceof ApiError);
            console.error('[API Utils] Error instanceof BadRequest:', error instanceof BadRequest);
            
            // Handle network errors (socket hang up, ECONNRESET, connection refused, etc.)
            if (error.name === 'TypeError' && (
                error.message.includes('fetch') ||
                error.message.includes('Failed to fetch') ||
                error.message.includes('NetworkError')
            )) {
                // An aborted request can surface as a TypeError instead of an
                // AbortError when the connection is torn down mid-flight. That is
                // the app superseding its own request, not a connection problem.
                if (params && params.signal && params.signal.aborted) {
                    console.log('[API Utils] Superseded request reported as fetch failure, no toast. URL:', url);
                    throw error;
                }
                console.error('[NETWORK_ERROR] Connection error:', error.message, 'URL:', url);
                getStore().dispatch(showToast('error', 'Ошибка соединения. Пожалуйста, попробуйте позже.'));
                throw error;
            }
            // Handle AbortError (timeout, cancelled requests).
            // A request the app itself superseded (e.g. a newer search keystroke)
            // is not a failure the user needs to see, so stay silent for those.
            if (error.name === 'AbortError') {
                if (params && params.signal && params.signal.aborted) {
                    console.log('[API Utils] Request superseded by a newer one, no toast. URL:', url);
                    throw error;
                }
                console.error('[NETWORK_ERROR] Request aborted:', error.message, 'URL:', url);
                getStore().dispatch(showToast('error', 'Запрос отменен или превышено время ожидания.'));
                throw error;
            }
            if (error instanceof ApiError) {
                console.log('[API Utils] Error is ApiError instance');
                console.log('[API Utils] Error.data:', error.data);
                console.log('[API Utils] Error.status:', error.status);
                
                // Safely get onClick path - handle case where error.data might be undefined
                let onClickPath = null;
                try {
                    if (error && error.data && typeof error.data === 'object') {
                        onClickPath = R.path(['data', '__onClick', 0], error);
                    }
                } catch (pathError) {
                    console.error('[API Utils] Error getting onClick path:', pathError);
                }
                
                // Use detailed message for BadRequest errors
                const message = (error instanceof BadRequest
                    ? error.getDetailedMessage()
                    : error.getMessage()) || 'Сервер временно недоступен. Пожалуйста, попробуйте позже.';
                const title = error instanceof BadRequest ? 'Ошибка валидации' : 'Ошибка';
                console.log('[API Utils] Showing toast with message:', message);

                // Don't show toast for 404 errors - they're handled by the component
                if (error.status !== 404) {
                    const toastOptions = {
                        timeOut: error instanceof BadRequest ? 10000 : 5000,
                    };
                    if (onClickPath) {
                        toastOptions.onclick = () => { window.location = onClickPath; };
                    }
                    getStore().dispatch(showToast('error', String(message), title, toastOptions));
                } else {
                    console.log('[API Utils] 404 error - not showing toast, component will handle it');
                }
            } else {
                console.error('[API_ERROR] Unknown error type:', error);
                console.error('[API_ERROR] Error keys:', error ? Object.keys(error) : 'error is null/undefined');
                getStore().dispatch(showToast('error', 'Неизвестная ошибка', 'Ошибка', { timeOut: 5000 }));
            }
            console.log('[API Utils] Re-throwing error for redux-promise-middleware');
            throw error;
        });
};


// Manual query string builder to replace wQuery (which has issues)
function buildQueryString(params) {
    if (!params || typeof params !== 'object' || Object.keys(params).length === 0) {
        return '';
    }
    return Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== undefined)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
}

export default {
    get: (url, params, options) => {
        // Build query string manually instead of using wQuery (which has issues)
        let finalUrl = url;
        if (params && typeof params === 'object' && Object.keys(params).length > 0) {
            const queryString = buildQueryString(params);
            if (queryString) {
                finalUrl = `${url}${url.indexOf('?') === -1 ? '?' : '&'}${queryString}`;
            }
        }
        
        console.log('[API utils.js] GET request', {
            originalUrl: url,
            params,
            finalUrl,
            queryString: params ? buildQueryString(params) : '',
            timestamp: new Date().toISOString()
        });
        const fetchParams = getFetchParams();
        if (options && options.signal) {
            fetchParams.signal = options.signal;
        }
        const promise = fetchUrl(finalUrl, fetchParams).then(response => {
            console.log('[API utils.js] GET response received', {
                url: finalUrl,
                response,
                timestamp: new Date().toISOString()
            });
            return commonProcess(response);
        }).then(data => {
            console.log('[API utils.js] GET response processed', {
                url: finalUrl,
                data,
                timestamp: new Date().toISOString()
            });
            return data;
        }).catch(error => {
            console.error('[API utils.js] GET request failed', {
                url: finalUrl,
                error,
                errorMessage: error.message,
                errorStack: error.stack,
                timestamp: new Date().toISOString()
            });
            throw error;
        });
        return promise;
    },
    put: (url, data, contentType) => fetchUrl(url, FETCH_PUT_PARAMS(data, contentType)).then(commonProcess),
    post: (url, data, contentType) => fetchUrl(url, FETCH_POST_PARAMS(data, contentType)).then(commonProcess),
    patch: (url, data) => fetchUrl(url, FETCH_PATCH_PARAMS(data)).then(commonProcess),
    upload: (url, data) => fetchUrl(url, FILE_UPLOAD_PARAMS(data)).then(commonProcess),
    delete: url => fetchUrl(url, FETCH_DELETE_PARAMS()),
    postSoft: (url, data) => fetchUrl(url, FETCH_POST_PARAMS(data)),
    formSubmit: ({url, data, method}) => fetch(url, FORM_METHOD(data, method)),
    handlePut: (url, data) => fetch(url, FETCH_PUT_PARAMS(data)),
    handlePost: (url, data) => fetch(url, FETCH_POST_PARAMS(data)),
    handleGet: (url, params) => fetch(url, getFetchParams()),
};



// WEBPACK FOOTER //
// ./src/api/utils.js